import React, { useMemo } from 'react';
import { Label } from '@atlaskit/form';
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import Select from '@atlaskit/select';
import { useIntl, type MessageDescriptor } from '@atlassian/jira-intl';
import {
	FireScreenAnalytics,
	ContextualAnalyticsData,
	DRAWER,
} from '@atlassian/jira-product-analytics-bridge';
import { SidebarDataHandler } from '../../common/async-handler/index.tsx';
import {
	FIELD_LIMIT,
	SingleSelectChartList,
	SingleSelectChart,
	type SingleSelectChartType,
} from '../../common/constants.tsx';
import { SidebarError } from '../../common/sidebar-error/index.tsx';
import type { DataType } from '../../common/types.tsx';
import { useSelectFieldStatistics } from '../../services/select-field/index.tsx';
import { DonutChart } from './donut-chart/index.tsx';
import { HorizontalBarChart } from './horizontal-bar-chart/index.tsx';
import messages from './messages.tsx';

type ChartTypeSelect = { label: string; value: SingleSelectChartType };

const CHART_LABELS: Record<SingleSelectChartType, MessageDescriptor> = {
	[SingleSelectChart.DONUT]: messages.donutChartLabel,
	[SingleSelectChart.HORIZONTAL_BAR]: messages.horizontalBarChartLabel,
};

const isValidChartType = (type: string): type is SingleSelectChartType => {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	return SingleSelectChartList.includes(type as SingleSelectChartType);
};

const ChartRenderer = ({
	data,
	chartType,
	width,
}: {
	data: DataType[] | undefined;
	width: number;
	chartType: string;
}) => {
	const { formatMessage } = useIntl();

	if (!data) return null;
	switch (chartType) {
		case SingleSelectChart.DONUT:
			return <DonutChart data={data} width={width} />;
		case SingleSelectChart.HORIZONTAL_BAR:
			return <HorizontalBarChart data={data} width={width} />;
		default:
			return <SidebarError errorMessage={formatMessage(messages.chartTypeErrorNonFinal)} />;
	}
};

type Props = {
	customFieldId: string;
	query: string;
	width: number;
	chartType: string;
	onChartTypeChange: (chartType: SingleSelectChartType) => void;
};

export const SingleSelectChartContainer = ({
	customFieldId,
	query,
	width,
	chartType: initialChartType,
	onChartTypeChange,
}: Props) => {
	const { formatMessage } = useIntl();
	const [{ loading, data, error }] = useSelectFieldStatistics({
		customFieldId,
		query,
		limit: FIELD_LIMIT,
	});

	const chartType: SingleSelectChartType = isValidChartType(initialChartType)
		? initialChartType
		: SingleSelectChart.DONUT;

	const options = useMemo(
		() =>
			SingleSelectChartList.map((type) => ({
				label: formatMessage(CHART_LABELS[type]),
				value: type,
			})),
		[formatMessage],
	);

	return (
		<SidebarDataHandler error={error} loading={loading} data={data} isEmpty={data?.length === 0}>
			<ContextualAnalyticsData
				sourceType={DRAWER}
				sourceName="jira.issueFieldStatistics.singleSelectChart"
				attributes={{
					chartType,
				}}
			>
				<FireScreenAnalytics />
				<Stack space="space.300">
					<Box paddingBlock="space.100">
						<Label
							htmlFor="issue-field-statistics-chart-type-select"
							id="issue-field-statistics-chart-type-select-label"
						>
							{formatMessage(messages.chartTypeSelectLabel)}
						</Label>
						<Select<ChartTypeSelect>
							value={{ label: formatMessage(CHART_LABELS[chartType]), value: chartType }}
							onChange={(newChartType) => {
								newChartType && onChartTypeChange(newChartType.value);
							}}
							options={options}
							id="issue-field-statistics-chart-type-select"
							labelId="issue-field-statistics-chart-type-select-label"
						/>
					</Box>
					<Inline alignInline="center" xcss={chartStyles}>
						<ChartRenderer data={data} width={width} chartType={chartType} />
					</Inline>
				</Stack>
			</ContextualAnalyticsData>
		</SidebarDataHandler>
	);
};

const chartStyles = xcss({
	paddingInline: 'space.150',
	paddingBlock: 'space.200',
});
